import { useCallback, useEffect, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import redirect from 'js/lib/coursera.redirect';
import { stringKeyToTuple } from 'js/lib/stringKeyTuple';

import { courseraPlusProductVariants } from 'bundles/coursera-plus/constants/CourseraPlusProductVariant';
import EnrollmentChoiceTypes from 'bundles/enroll-course/common/EnrollmentChoiceTypes';
import { submitEnrollmentPromise } from 'bundles/enroll-course/lib/enrollmentChoiceUtils';
import { choiceTypeToHandleSubmitPromise } from 'bundles/enroll-course/lib/enrollmentChoiceUtils';
import ScopedContentRulesQuery from 'bundles/enroll/utils/ScopedContentRulesQuery.graphql';
import { policies, scopes } from 'bundles/enroll/utils/scopedContentRule';
import PaymentsExperiments from 'bundles/epic/clients/payments';
import type SubscriptionV1 from 'bundles/naptimejs/resources/subscriptions.v1';
import GetSpecializationCertificate from 'bundles/subscriptions/queries/GetSpecializationCertificate.graphql';
import type {
  GetSpecializationCertificateQuery,
  GetSpecializationCertificateQueryVariables,
} from 'bundles/subscriptions/queries/__generated__/GetSpecializationCertificate';
import { isStripeSubscription } from 'bundles/subscriptions/utils/isStripeSubscription';

export const generateImpressionForResubscribeAfterCancellationPeriod = (
  resubscriptionEligibleSubscriptions: SubscriptionV1[]
) => {
  if (resubscriptionEligibleSubscriptions.length > 0) {
    PaymentsExperiments.get('resubscribeAfterCancellationPeriod');
  }
};

export const previewResubscribeAfterCancellationPeriod = () =>
  PaymentsExperiments.preview('resubscribeAfterCancellationPeriod');

export const useResubscriptionAfterCancellationPeriod = (subscriptions: SubscriptionV1[]) => {
  const [resubscriptionEligibleSubscriptions, setResubscriptionEligibleSubscriptions] = useState<SubscriptionV1[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [executeQuery] = useLazyQuery<GetSpecializationCertificateQuery, GetSpecializationCertificateQueryVariables>(
    GetSpecializationCertificate,
    {
      context: { clientName: 'gatewayGql' },
    }
  );

  const [executeScopedContentRuleQuery] = useLazyQuery(ScopedContentRulesQuery, {
    context: { clientName: 'gatewayGql' },
  });

  const getS12nCertificate = useCallback<(s12nId: string) => Promise<string | null | undefined>>(
    async (s12nId) => {
      const { data, error } = await executeQuery({ variables: { s12NId: s12nId } });
      if (error) {
        Sentry.captureException(error);
        return null;
      }
      return data?.SpecializationProgress?.progress?.certificateMetadata?.grantedAt;
    },
    [executeQuery]
  );

  const isS12nInCPlus = useCallback<(s12nId: string) => Promise<boolean>>(
    async (s12nId) => {
      const { data } = await executeScopedContentRuleQuery({
        variables: {
          input: { scope: scopes.COURSERA_PLUS_SUBSCRIPTION, productType: 'Specialization', productItemId: s12nId },
        },
      });
      return data?.ScopedContentRule?.getRule?.rule?.policy === policies.ALLOW;
    },
    [executeScopedContentRuleQuery]
  );

  const hasActiveCPlusSubscription = (subscriptions: SubscriptionV1[]) =>
    subscriptions?.some(({ isCourseraPlusSubscription, isActive }) => isCourseraPlusSubscription && isActive);

  const checkSubscriptions = useCallback<() => Promise<SubscriptionV1[]>>(async () => {
    const hasActiveCPlus = hasActiveCPlusSubscription(subscriptions);

    const results = await Promise.all(
      subscriptions.map(async (subscription) => {
        // Only INACTIVE subscriptions are eligible
        if (!subscription.isInactive) return false;

        // Omit non-stripe subscriptions
        if (!isStripeSubscription(subscription)) return false;

        if (subscription.isSpecializationSubscription) {
          // If they have an active CPlus subscription, check if the specialization is included in CPlus
          if (hasActiveCPlus && (await isS12nInCPlus(subscription.productItemId))) {
            return false;
          }

          // If it's an S12n, check for completion.
          const certificate = await getS12nCertificate(subscription.productItemId);

          // If the S12n has not been completed, it's eligible for resubscription.
          if (!certificate) return true;
        }

        // If they don't have CPlus, inactive CPlus subscriptions are eligible for resubscription
        if (!hasActiveCPlus && subscription.isCourseraPlusSubscription) {
          return true;
        }

        return false;
      })
    );

    return subscriptions.filter((_, index) => results[index]);
  }, [subscriptions, getS12nCertificate, isS12nInCPlus]);

  useEffect(() => {
    setIsLoading(true);
    checkSubscriptions()
      .then((eligible) => {
        setResubscriptionEligibleSubscriptions(eligible);
      })
      .catch((error) => {
        setResubscriptionEligibleSubscriptions([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [checkSubscriptions]);

  const isResubscriptionAfterCancellationEligible = useCallback<(subscriptionId: string) => boolean>(
    (subscriptionId) =>
      resubscriptionEligibleSubscriptions.some((subscription) => subscription.productItemId === subscriptionId),
    [resubscriptionEligibleSubscriptions]
  );

  return {
    resubscriptionEligibleSubscriptions,
    isLoading,
    isResubscriptionAfterCancellationEligible,
  };
};

export const redirectToCheckout = (cartId: number) => {
  redirect.setLocation(`/payments/checkout?cartId=${cartId}&isResubscription=true`);
};

/*
  For resubscriptions to CPlus, map upsell/midcycle upgrade variants to the correct monthly/annual product id
*/
const mapCourseraPlusProductId = (productId: string) => {
  if (productId === courseraPlusProductVariants.ANNUAL_MIDCYCLE_UPGRADE) {
    return courseraPlusProductVariants.ANNUAL_NO_FREE_TRIAL;
  }

  if (
    productId === courseraPlusProductVariants.MONTHLY_UPSELL_UPGRADE ||
    productId === courseraPlusProductVariants.MONTHLY_MIDCYCLE_UPSELL_UPGRADE
  ) {
    return courseraPlusProductVariants.MONTHLY_WITH_FREE_TRIAL;
  }

  return productId;
};

/**
 * Creates a cart for the item we're resubscribing for.
 * @param subscription - The subscription to create a cart for.
 * @returns CartV1 - The cart created if it's successful, otherwise null.
 */
export const createResubscriptionCart = async (subscription: SubscriptionV1, s12nId?: string, courseId?: string) => {
  const { productItemId, productSku, isCourseraPlusSubscription, isSpecializationSubscription } = subscription;

  const [, productId] = productSku ? stringKeyToTuple(productSku) : [];

  if (!subscription.isCourseraPlusSubscription && !subscription.isSpecializationSubscription) {
    throw new Error('Unsupported product type for resubscription');
  }

  if (isCourseraPlusSubscription) {
    const mappedProductId = mapCourseraPlusProductId(productId);
    const { cart } = await submitEnrollmentPromise({
      handleSubmitPromise: choiceTypeToHandleSubmitPromise[EnrollmentChoiceTypes.SUBSCRIBE_TO_COURSERA_PLUS],
      options: {
        productItemId: mappedProductId || productItemId,
        isSubscriptionOnly: Boolean(!courseId && !s12nId),
        courseId,
        s12nId,
      },
      skipCheckoutRedirect: true,
    });

    if (cart) {
      return cart;
    }
  } else if (isSpecializationSubscription) {
    const { cart } = await submitEnrollmentPromise({
      handleSubmitPromise: choiceTypeToHandleSubmitPromise[EnrollmentChoiceTypes.ENROLL_THROUGH_S12N_SUBSCRIPTION],
      options: {
        productSkuId: productId,
        courseId,
      },
      skipCheckoutRedirect: true,
    });

    if (cart) {
      return cart;
    }
  }
};
